import { FC, useState } from 'react';

import { SearchIcon } from '@heroicons/react/outline';
import {
  GiftCouponForRedemption,
  GiftCouponRedeemStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Image } from '@shared/api/media/media.interface';
import { Link } from 'react-router-dom';

import { KakaoShareCallbackModal } from '../components/couponDetail/KakaoShareCallbackModal';
import type { VerificationData } from '../components/couponSearch';
import {
  BlacklistSection,
  LaterReceiveModal,
  SearchForm,
  SearchResultsTable,
  StatsCards,
} from '../components/couponSearch';
import { OrdererOrdersModal } from '../components/OrdererOrdersModal';
import { SignatureViewModal } from '../components/SignatureViewModal';
import { Toast } from '../components/Toast';
import { VerificationViewModal } from '../components/VerificationViewModal';
import { useCouponHistorySearch } from '../hooks/useCouponHistorySearch';

export const GiftCouponHistorySearchPage: FC = function GiftCouponHistorySearchPage() {
  const hook = useCouponHistorySearch();
  const [signatureModalImage, setSignatureModalImage] = useState<Image | null>(null);
  const [verificationModal, setVerificationModal] = useState<VerificationData | null>(
    null,
  );
  const [kakaoCallbackCoupon, setKakaoCallbackCoupon] =
    useState<GiftCouponForRedemption | null>(null);
  // 주문검색 모달 — 쿠폰 행에서 열면 그 쿠폰이 든 주문을 강조 대상으로 함께 넘긴다
  const [ordererOrdersTarget, setOrdererOrdersTarget] = useState<{
    phoneNumber: string;
    orderId?: string;
    label?: string;
  } | null>(null);
  // 신청됨 ↔ 나중에 받기 전환 대상
  const [laterReceiveCoupon, setLaterReceiveCoupon] =
    useState<GiftCouponForRedemption | null>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">쿠폰 내역 검색</h1>
            <p className="mt-1 text-sm text-gray-500">
              기간, 전화번호, 주문자명 등으로 쿠폰 내역을 검색하세요.
            </p>
          </div>
          <Link
            to="/gift-seller/coupon-redemption"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            발송 대기 목록
          </Link>
        </div>
      </div>

      {/* Stats */}
      <StatsCards
        statistics={hook.statistics}
        showBlacklist={hook.showBlacklist}
        onToggleBlacklist={() => hook.setShowBlacklist((prev) => !prev)}
        onSearchByStatus={hook.searchByStatus}
        redeemStatus={hook.redeemStatus}
        hasSearched={hook.hasSearched}
      />

      {/* 블랙리스트 */}
      {hook.showBlacklist &&
        hook.statistics?.blacklist &&
        hook.statistics.blacklist.length > 0 && (
          <BlacklistSection
            blacklist={hook.statistics.blacklist}
            onCopyToClipboard={hook.copyToClipboard}
            onSearchByPhone={hook.searchByPhone}
          />
        )}

      {/* 검색 폼 */}
      <SearchForm
        fullCode={hook.fullCode}
        onFullCodeChange={hook.setFullCode}
        phoneNumber={hook.phoneNumber}
        onPhoneNumberChange={hook.setPhoneNumber}
        ordererName={hook.ordererName}
        onOrdererNameChange={hook.setOrdererName}
        ordererPhone={hook.ordererPhone}
        onOrdererPhoneChange={hook.setOrdererPhone}
        redeemStatus={hook.redeemStatus}
        onRedeemStatusChange={hook.setRedeemStatus}
        shareStatus={hook.shareStatus}
        onShareStatusChange={hook.setShareStatus}
        dateField={hook.dateField}
        onDateFieldChange={hook.setDateField}
        dateFrom={hook.dateFrom}
        onDateFromChange={hook.setDateFrom}
        dateTo={hook.dateTo}
        onDateToChange={hook.setDateTo}
        onSearch={hook.handleSearch}
        onReset={hook.handleReset}
        onKeyDown={hook.handleKeyDown}
      />

      {/* 검색 결과 */}
      <div className="p-4">
        {hook.hasSearched ? (
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <SearchResultsTable
              hook={hook}
              onOpenSignatureModal={setSignatureModalImage}
              onOpenVerificationModal={setVerificationModal}
              onOpenKakaoShareCallbacks={setKakaoCallbackCoupon}
              onOpenOrderModalByPhone={(phoneNumber, highlight) =>
                setOrdererOrdersTarget({ phoneNumber, ...highlight })
              }
              onOpenLaterReceive={setLaterReceiveCoupon}
            />
          </div>
        ) : (
          <div className="rounded-lg bg-white p-12 text-center shadow">
            <SearchIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">쿠폰 내역 검색</h3>
            <p className="mt-1 text-sm text-gray-500">
              검색 조건을 입력하고 검색 버튼을 눌러주세요.
            </p>
          </div>
        )}
      </div>

      <Toast message={hook.toastMessage} durationMs={hook.toastDurationMs} />

      {signatureModalImage && (
        <SignatureViewModal
          signatureImage={signatureModalImage}
          onClose={() => setSignatureModalImage(null)}
        />
      )}

      {verificationModal && (
        <VerificationViewModal
          verification={verificationModal}
          onClose={() => setVerificationModal(null)}
        />
      )}

      {kakaoCallbackCoupon && (
        <KakaoShareCallbackModal
          coupon={kakaoCallbackCoupon}
          onClose={() => setKakaoCallbackCoupon(null)}
        />
      )}

      {ordererOrdersTarget && (
        <OrdererOrdersModal
          phoneNumber={ordererOrdersTarget.phoneNumber}
          highlightOrderId={ordererOrdersTarget.orderId}
          highlightLabel={ordererOrdersTarget.label}
          onClose={() => setOrdererOrdersTarget(null)}
        />
      )}

      {laterReceiveCoupon && (
        <LaterReceiveModal
          coupon={laterReceiveCoupon}
          isProcessing={hook.isConvertingLater}
          onConfirm={(reason) =>
            laterReceiveCoupon.redeemStatus === GiftCouponRedeemStatus.Requested
              ? hook.handleConvertToLater(laterReceiveCoupon, reason)
              : hook.handleRestoreToRequested(laterReceiveCoupon, reason)
          }
          onClose={() => setLaterReceiveCoupon(null)}
        />
      )}
    </div>
  );
};
